import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Printer, Truck, Package, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["pending", "paid", "shipped", "delivered", "cancelled"];
const CARRIERS = [
  { value: "usps", label: "USPS" },
  { value: "ups", label: "UPS" },
  { value: "fedex", label: "FedEx" },
  { value: "dhl", label: "DHL" },
  { value: "other", label: "Other" },
];

const carrierUrl = (carrier, number) => {
  if (!number) return null;
  const n = number.trim();
  switch ((carrier || "").toLowerCase()) {
    case "usps": return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`;
    case "ups": return `https://www.ups.com/track?tracknum=${n}`;
    case "fedex": return `https://www.fedex.com/fedextrack/?trknbr=${n}`;
    case "dhl": return `https://www.dhl.com/us-en/home/tracking/tracking-express.html?tracking-id=${n}`;
    default: return null;
  }
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : "";
      const r = await api.get(`/admin/orders${params}`);
      setOrders(r.data);
    } catch (e) { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (oid, patch) => {
    try {
      await api.put(`/admin/orders/${oid}/status`, patch);
      if (patch.shipping_status === "shipped" && patch.tracking) {
        toast.success("Marked shipped — buyer notified");
      } else {
        toast.success("Order updated");
      }
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-4xl">Orders</h1>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="orders-filter"
                className="bg-[var(--cl-surface)] border border-white/10 rounded px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      {loading && <div className="text-zinc-500">Loading…</div>}
      {!loading && orders.length === 0 && <div className="text-zinc-500">No orders yet.</div>}

      <div className="space-y-3">
        {orders.map((o) => (
          <OrderCard key={o.id} order={o} onUpdate={updateStatus} />
        ))}
      </div>
    </div>
  );
}

function OrderCard({ order: o, onUpdate }) {
  const trackUrl = carrierUrl(o.carrier, o.tracking);
  return (
    <div className="border border-white/10 rounded p-4 bg-[var(--cl-surface)]" data-testid={`order-${o.id}`}>
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div>
          <div className="text-label text-zinc-500">Order · {o.created_at?.slice(0, 10)}</div>
          <div className="text-sm mt-1">{o.items?.length || 0} item(s) · User: <span className="text-zinc-400">{o.user_id || "guest"}</span></div>
          <ul className="text-xs text-zinc-400 mt-2 space-y-1">
            {o.items?.map((it) => (
              <li key={it.product_id}>{it.quantity} × {it.title} — ${it.price}</li>
            ))}
          </ul>
          {o.tracking && (
            <div className="text-xs text-[var(--cl-orange)] mt-2 flex items-center gap-2">
              <Truck size={12}/>
              <span>{(o.carrier || "carrier").toUpperCase()}: {o.tracking}</span>
              {trackUrl && (
                <a href={trackUrl} target="_blank" rel="noreferrer" className="underline text-zinc-300 hover:text-white"
                   data-testid={`admin-track-link-${o.id}`}>
                  Open →
                </a>
              )}
              {o.buyer_shipped_email_sent && (
                <span className="text-zinc-500">· buyer notified</span>
              )}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="font-display text-3xl">${o.total?.toFixed(2)}</div>
          <div className="text-xs text-zinc-500 mt-1">Payment: {o.payment_status}</div>
          <Link to={`/admin/orders/${o.id}/slip`} target="_blank"
                data-testid={`slip-${o.id}`}
                className="inline-flex items-center gap-1 mt-2 text-xs text-[var(--cl-orange)] hover:underline">
            <Printer size={12}/> Packing slip
          </Link>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/5 items-center">
        <span className="text-label text-zinc-500 mr-2">Ship status:</span>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => onUpdate(o.id, { shipping_status: s })}
                  data-testid={`set-${o.id}-${s}`}
                  className={`text-xs px-3 py-1 rounded border transition-colors ${o.shipping_status === s ? "bg-[var(--cl-primary)] border-[var(--cl-primary)] text-white" : "border-white/15 text-zinc-300 hover:border-[var(--cl-orange)]"}`}>
            {s}
          </button>
        ))}
      </div>
      <TrackingBar order={o} onShip={onUpdate} />
    </div>
  );
}

function TrackingBar({ order: o, onShip }) {
  const [number, setNumber] = useState(o.tracking || "");
  const [carrier, setCarrier] = useState(o.carrier || "usps");
  const [buying, setBuying] = useState(false);

  const shipAndNotify = () => {
    const n = number.trim();
    if (!n) { toast.error("Enter a tracking number first"); return; }
    onShip(o.id, { shipping_status: "shipped", tracking: n, carrier });
  };
  const saveOnly = () => {
    onShip(o.id, { shipping_status: o.shipping_status, tracking: number.trim() || null, carrier });
  };
  const buyLabel = async () => {
    if (o.tracking) return toast.error("Order already has a tracking number");
    if (!window.confirm(
`⚠️  BUY REAL POSTAGE

You are about to purchase a USPS Ground Advantage label for order ${o.id}.

Your Shippo account will be charged ~$3.65–$5.20 in REAL money.

Continue?`
    )) return;
    setBuying(true);
    try {
      const r = await api.post(`/admin/orders/${o.id}/buy-label`);
      toast.success(`Label bought · $${r.data.amount} · buyer notified`);
      window.open(r.data.label_url, "_blank", "noopener,noreferrer");
      // parent will reload on next tick
      setTimeout(() => onShip(o.id, { shipping_status: "shipped" }), 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Label purchase failed");
    } finally { setBuying(false); }
  };

  return (
    <div className="mt-3 flex flex-wrap gap-2 items-center">
      <span className="text-label text-zinc-500 mr-2">Tracking:</span>
      <select value={carrier} onChange={(e) => setCarrier(e.target.value)}
              data-testid={`carrier-${o.id}`}
              className="text-xs bg-black border border-white/10 rounded px-2 py-1">
        {CARRIERS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
      <input placeholder="Tracking #" value={number} onChange={(e) => setNumber(e.target.value)}
             data-testid={`tracking-${o.id}`}
             className="text-xs bg-black border border-white/10 rounded px-2 py-1 w-40" />
      <button onClick={shipAndNotify} data-testid={`ship-notify-${o.id}`}
              className="text-xs bg-[var(--cl-primary)] hover:bg-[var(--cl-primary-hover,#e6342a)] text-white rounded px-3 py-1 font-semibold">
        Ship &amp; Notify Buyer
      </button>
      <button onClick={saveOnly} data-testid={`tracking-save-${o.id}`}
              className="text-xs btn-outline px-3 py-1">Save only</button>
      <span className="mx-2 text-zinc-600">|</span>
      <button onClick={buyLabel} disabled={buying || !!o.tracking}
              data-testid={`buy-label-${o.id}`}
              title={o.tracking ? "This order already has tracking" : "Buy USPS Ground Advantage label via Shippo"}
              className="text-xs bg-[var(--cl-orange)] hover:bg-orange-500 text-black rounded px-3 py-1 font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
        {buying ? <><Loader2 size={12} className="inline mr-1 animate-spin -mt-0.5" /> Buying…</> : <><Package size={12} className="inline mr-1 -mt-0.5" /> Buy Label</>}
      </button>
      {o.label_url && (
        <a href={o.label_url} target="_blank" rel="noreferrer"
           data-testid={`reprint-label-${o.id}`}
           className="text-xs underline text-zinc-400 hover:text-white">Reprint PDF</a>
      )}
    </div>
  );
}
